import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FileSignature, Calendar, Activity } from 'lucide-react';

export default function Prescriptions() {
  const { token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [cases, setCases] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        
        // Fetch sessions
        const sessionRes = await axios.get(`${apiUrl}/api/v1/clinicalsim/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const sessionList = sessionRes.data?.sessions || [];
        
        // Only keep sessions that have a prescription submitted
        setSessions(sessionList.filter(s => s.prescription));

        // Fetch cases
        const caseRes = await axios.get(`${apiUrl}/api/v1/clinicalsim/cases`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const caseMap = {};
        caseRes.data?.cases?.forEach(c => {
          caseMap[c.case_id] = c;
        });
        setCases(caseMap);

      } catch (err) {
        console.error('Failed to load prescriptions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Prescriptions Archive</h1>
        <p className="text-slate-500 mt-1">Review your submitted clinical findings and treatment plans.</p>
      </div>

      {sessions.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center shadow-sm">
          <FileSignature className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-700 mb-2">No prescriptions yet</h2>
          <p className="text-slate-500">Complete a simulation case and submit your findings to see them here.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sessions.map((session) => {
            const caseInfo = cases[session.case_id] || { title: 'Unknown Case', patient: {} };
            const p = session.prescription;
            
            return (
              <div key={session.session_id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 font-bold text-sm shadow-sm">
                      {caseInfo.patient?.initials || 'PT'}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800 text-lg">{caseInfo.title}</h3>
                      <p className="text-xs text-slate-500 font-medium">{caseInfo.patient?.name} • Attempt #{session.attempt_number}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-slate-500">
                    <Calendar className="w-4 h-4" />
                    {new Date(session.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Left Column */}
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Chief Complaint</h4>
                      <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-100 leading-relaxed">
                        {p.chief_complaint || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">History of Present Illness</h4>
                      <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-100 leading-relaxed whitespace-pre-wrap">
                        {p.history_summary || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <Activity className="w-3.5 h-3.5 text-violet-500" />
                        Provisional Diagnosis
                      </h4>
                      <div className="bg-violet-50 text-violet-800 p-3 rounded-xl border border-violet-100 flex items-center gap-3">
                        {p.provisional_diagnosis_code && (
                          <span className="px-2 py-1 bg-white rounded font-mono text-xs font-bold text-violet-600 shadow-sm">
                            {p.provisional_diagnosis_code}
                          </span>
                        )}
                        <span className="text-sm font-medium">
                          {p.provisional_diagnosis_label || 'No diagnosis provided'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Treatment Plan / Notes</h4>
                      <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-100 leading-relaxed whitespace-pre-wrap">
                        {p.additional_notes || 'No additional notes provided.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
